/**
 * Every team a signed-in person can act under, and which one is chosen.
 *
 * The bug this fixes: `apps/api/src/routes/team.ts` has served
 * `GET /api/team/memberships` since the first migration, filtered to the roles
 * a person can actually act under, and no screen in this app ever called it.
 * `resolveMembership` on the server picks ONE team by season recency when the
 * request names none, so a person on two teams could only ever reach whichever
 * team's season started later, and the other was unreachable on any day of the
 * year. This hook is the client half that was missing.
 *
 * The selection lives in the URL, not in storage: `?team=<uuid>` on the page.
 * There is no key-value storage adapter in `packages/platform` (its `storage`
 * module is about persistence and quota, not preferences), and
 * `MOBILE_SUPPLEMENT.md` 2.1 forbids reaching `localStorage` directly from
 * feature code. The URL survives a reload, is shareable, and back and forward
 * work, none of which a storage key buys for free.
 */

import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

import { api } from '../api/client.js';

export interface TeamMembership {
  readonly teamId: string;
  readonly teamName: string;
  readonly organizationName?: string;
  readonly seasonId: string;
  readonly seasonName: string;
  readonly role: string;
}

/**
 * No `error` payload carried here, deliberately.
 *
 * The only consumer of this state is a switcher that is free to disappear:
 * `AppShell` is required to keep rendering the app on this state (see its own
 * comment), and the team the server already resolved keeps working with no
 * memberships list at all. There is nothing for a themed error screen to say
 * that would not just be noise on top of a shell that is otherwise fine, so
 * this carries no message for one to render by mistake.
 */
export type TeamsState =
  | { readonly kind: 'loading' }
  | {
      readonly kind: 'ready';
      readonly memberships: readonly TeamMembership[];
      /** Team selected by the server when this page has no `?team=` selection. */
      readonly currentTeamId?: string;
      /** Owns an archived team it could restore from Admin (ADR-0095). */
      readonly canRestoreTeams: boolean;
    }
  | { readonly kind: 'error' };

export interface UseTeamsResult {
  readonly state: TeamsState;
  /** From `?team=` on the page. Undefined means "let the server choose." */
  readonly selectedTeamId: string | undefined;
  /**
   * Writes `?team=` on the page.
   *
   * Does not itself refetch anything: `AppRoutes` in `App.tsx` keys the routed
   * screen on this same value, so changing it remounts whichever screen is
   * showing and its existing on-mount fetch effect runs again, this time with
   * the new team on the URL. Nothing in this hook assumes what the current
   * screen does with the choice.
   */
  readonly selectTeam: (teamId: string) => void;
}

interface MembershipsResponse {
  readonly memberships: readonly TeamMembership[];
  readonly currentTeamId?: unknown;
  /** Absent from a server older than ADR-0095, which reads as false. */
  readonly canRestoreTeams?: unknown;
}

/**
 * A guard, not a cast.
 *
 * `api<MembershipsResponse>()` types the answer, it does not check it: a
 * response that is JSON but is not actually this shape (a stale cached bundle
 * talking to a newer or misbehaving server, a proxy's own JSON error page)
 * would otherwise reach `TeamSwitcher` as `{ kind: 'ready', memberships:
 * undefined }` and throw on `.length` rendering the very shell this hook is
 * required not to break.
 */
function hasMembershipsArray(value: unknown): value is MembershipsResponse {
  return (
    typeof value === 'object' &&
    value !== null &&
    Array.isArray((value as { memberships?: unknown }).memberships)
  );
}

/**
 * A membership entry this screen can actually render and act on.
 *
 * The array guard alone stopped the crash it was written for and not the
 * malformed-entry case: `memberships: [{}, {}]` passed it, rendered two rows
 * with an undefined key and no name, and selecting one wrote `?team=undefined`,
 * which refuses every request and leaves the same broken list as the only way
 * back. Found by the fresh-context pass.
 */
function isUsableMembership(value: unknown): value is TeamMembership {
  if (typeof value !== 'object' || value === null) return false;
  const entry = value as { teamId?: unknown; teamName?: unknown; organizationName?: unknown };
  return (
    typeof entry.teamId === 'string' &&
    entry.teamId !== '' &&
    typeof entry.teamName === 'string' &&
    (entry.organizationName === undefined || typeof entry.organizationName === 'string')
  );
}

export function useTeams(): UseTeamsResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState<TeamsState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;

    api<MembershipsResponse>('/api/team/memberships').then(
      (response) => {
        if (cancelled) return;
        if (!hasMembershipsArray(response)) {
          setState({ kind: 'error' });
          return;
        }
        // Entries the screen cannot render or act on are dropped rather than
        // shown as blank rows that write `?team=undefined` when tapped.
        setState({
          kind: 'ready',
          memberships: response.memberships.filter(isUsableMembership),
          ...(typeof response.currentTeamId === 'string'
            ? { currentTeamId: response.currentTeamId }
            : {}),
          canRestoreTeams: response.canRestoreTeams === true,
        });
      },
      () => {
        // A person who cannot list their teams can still use the one the
        // server gave them: see the AppShell comment this hook's header
        // points at. Nothing here blocks or blanks anything, so there is no
        // retry to offer and no diagnostic to show.
        if (!cancelled) setState({ kind: 'error' });
      },
    );

    return () => {
      cancelled = true;
    };
    // Runs once per mount. `AppShell` is re-instantiated by every screen that
    // renders it (`TeamScreen`, `ScheduleScreen`, `Landing` each render their
    // own `<AppShell>`), so this already asks again on every navigation
    // between them, the same way each of those screens already re-asks
    // `/api/team` on its own mount. A cache spanning the whole session was
    // considered and left out: nothing else in this app has one yet, and
    // adding the first would be a second data-fetching pattern for a
    // problem this codebase does not otherwise treat as one.
  }, []);

  const selectedTeamId = searchParams.get('team') || undefined;

  const selectTeam = useCallback(
    (teamId: string) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          next.set('team', teamId);
          return next;
        },
        // Not a history entry per switch. Landing back on a team screen and
        // pressing Back should undo the navigation that brought somebody
        // here, not silently flip them back to a team they just left.
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return { state, selectedTeamId, selectTeam };
}
